/**
 * Accertify Data Collector
 * Captures device fingerprint data and syncs Transaction ID to cart attributes
 */

import { getCookie, setCookie } from '@theme/utilities';

/**
 * Accertify window interface with device data collector
 * @typedef {Window & {
 *   accertify_tid?: string,
 *   _bcn?: {
 *     dvc: {
 *       getTID: () => string,
 *       setSubmissionCallback: (callback: () => void) => void
 *     }
 *   },
 * }} AccertifyWindow
 */

const namespace = '[AccertifyDataCollector]';
const TID_COOKIE = 'bodi-accertify-tid';
const DATA_COLLECTOR_URL = window.BODi?.configs?.accertify?.dataCollectorUrl 
  || 'https://test.accdab.net/cdn/cs/WUWQmMNVRV4I3Iq65vKa6JqfTTA.js';

/**
 * Get cached TID from session cookie
 * @returns {string | null}
 */
function getCachedTID() {
  return getCookie(TID_COOKIE) || null;
}

/**
 * Cache TID in session cookie
 * @param {string} tid - Transaction ID
 */
function cacheTID(tid) {
  setCookie(TID_COOKIE, tid);
}

// Module-scoped variables for cached TID and existing TID
/** @type {string | null} */
let cachedTid = null;
/** @type {string | null} */
let existingCartTid = null;

async function initAccertify() {
  // Read the TID already stored in cart attributes (from Liquid)
  const scriptElement = document.querySelector("script[data-accertify-tid]");
  const scriptTid =
    scriptElement instanceof HTMLScriptElement ? scriptElement.dataset.accertifyTid : null;
  existingCartTid = scriptTid === "null" ? null : (scriptTid ?? null);

  // Check session cookie for cached TID
  cachedTid = getCachedTID();

  // Invalidate cached TID if it doesn't match the one stored in cart attributes
  if (cachedTid && cachedTid !== existingCartTid) {
    console.log(`${namespace} - Cached TID (${cachedTid}) does not match cart attribute TID (${existingCartTid}), invalidating`);
    cachedTid = null;
  }

  if (cachedTid) {
    console.log(`${namespace} - Using cached TID: ${cachedTid}`);
    const w = /** @type {AccertifyWindow} */ (window);
    w.accertify_tid = cachedTid;

    // Cached TID is already available; do not reload data collector script
    return;
  } else {
    console.log(`${namespace} - No valid cached TID, generating new TID`);
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = DATA_COLLECTOR_URL;
  script.id = 'bcn';
  script.setAttribute('dvc', 'a');

  script.onload = function () {
    const w = /** @type {AccertifyWindow} */ (window);
    if (w._bcn?.dvc) {
      w._bcn.dvc.setSubmissionCallback(registeredCallBack);
    }
  };

  script.onerror = function () {
    console.error(`${namespace} - Failed to load data collector script: ${DATA_COLLECTOR_URL}`);
  };

  document.head.appendChild(script);
}

async function registeredCallBack() {
  const w = /** @type {AccertifyWindow} */ (window);
  if (!w._bcn?.dvc) return;

  // This will be the cachedTID if it was set as the `dvci` script attribute, or a new TID otherwise
  const tid = w._bcn.dvc.getTID();
  if (!tid) return;

  console.log(`${namespace} - Device data with transaction ID: ${tid} was received successfully`);

  // Cache TID in session cookie
  cacheTID(tid);
  cachedTid = tid;
  console.log(`${namespace} - TID cached in session cookie`);

  // Only update cart attributes if TID is not already set
  if (tid !== existingCartTid && typeof fetch === 'function') {
    console.log(`${namespace} - Updating cart attributes with new TID`);
    const formData = new URLSearchParams();
    formData.append("attributes[accertify_tid]", tid);
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
      body: formData,
    });
  } else if (tid === existingCartTid) {
    console.log(`${namespace} - TID already set on cart, skipping update`);
  }

  w.accertify_tid = tid;
}

initAccertify();

/**
 * Wait for Accertify data collection to complete
 * Used in checkout-start.js
 */
export async function waitForAccertifyTID(timeout = 2500) {
  const startTime = Date.now();

  return new Promise((resolve) => {
    const checkInterval = setInterval(() => {
      const w = /** @type {AccertifyWindow} */ (window);

      if (w.accertify_tid) {
        clearInterval(checkInterval);
        console.info(`${namespace} - TID ready (cached): ${w.accertify_tid}`);
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        clearInterval(checkInterval);
        console.warn(`${namespace} - TID timeout after ${timeout}ms, proceeding anyway`);
        resolve(false);
      }
    }, 100);
  });
}
